import React from "react";
import { useFela } from "react-fela";

import { useTheme } from "@/hooks/useTheme";
import { Grid } from "@/components/Grid";
import { Heading } from "@/components/Heading";
import { Image } from "@/components/Image";
import { LinkButton } from "@/components/LinkButton";

import { IInspectorTopSection } from "./types";

type IProps = IInspectorTopSection & JSX.IntrinsicElements["div"];

export const InspectorTopSection: React.FC<IProps> = ({
  title,
  subtitle,
  description,
  image,
  buttonLabel,
  buttonHref,
  buttonStyle = {},
  wrapperStyle = {},
  ...rest
}) => {
  const { css } = useFela();
  const { bp, colors } = useTheme();

  return (
    <Grid
      id="inspector-hero"
      customStyle={{
        flexDirection: "column",
        padding: "200px 20px 40px",
        [bp.FROM_DESKTOP]: {
          padding: "180px 20px 80px",
        },
      }}
      wrapperStyle={{
        backgroundImage: `url('/images/inspector-mesh-bg.jpg')`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        [bp.FROM_DESKTOP]: {
          backgroundImage: `url('/images/inspector-lines-bg.svg'), url('/images/inspector-mesh-bg.jpg')`,
          backgroundPosition: "left 0% top 60%, center -320px",
          backgroundSize: "100%, cover",
        },
        ...wrapperStyle,
      }}
      {...rest}
    >
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        })}
      >
        <Heading
          as="h1"
          customStyle={{
            fontSize: 36,
            lineHeight: "48px",
            marginBottom: 18,
            letterSpacing: "-0.01rem",
            color: colors.DARK_GREY,
            fontWeight: 700,
            [bp.FROM_DESKTOP]: {
              maxWidth: 850,
              fontSize: 64,
              lineHeight: "74px",
              marginBottom: 24,
            },
          }}
        >
          {title}
        </Heading>
        <Heading
          as="h2"
          customStyle={{
            fontSize: 18,
            lineHeight: "24px",
            color: colors.DARK_GREY,
            textAlign: "center",
            fontWeight: 700,
            marginBottom: 18,
            [bp.FROM_DESKTOP]: {
              maxWidth: 770,
              fontSize: 24,
              lineHeight: "32px",
              marginBottom: 24,
            },
          }}
        >
          {subtitle}
        </Heading>
        <p
          className={css({
            textAlign: "center",
            fontSize: 18,
            marginBottom: 20,
            lineHeight: "28px",
            letterSpacing: ".5px",
            color: colors.GRAY,
            [bp.FROM_TABLET]: {
              maxWidth: 770,
              marginBottom: 40,
            },
          })}
        >
          {description}
        </p>

        <div
          className={css({
            [bp.FROM_TABLET]: {
              display: "flex",
              flexDirection: "row",
            },
          })}
        >
          <LinkButton
            href={buttonHref}
            customStyle={{
              padding: "10px 24px",
              backgroundColor: colors.ORANGE,
              color: colors.WHITE,
              marginBottom: 40,
              [bp.FROM_TABLET]: {
                marginBottom: 60,
              },
              ...buttonStyle,
            }}
            title={buttonLabel}
          />
        </div>

        <Image
          customStyle={{
            maxWidth: 970,
            width: "100%",
            margin: "0 auto",
            borderRadius: 16,
            filter: "drop-shadow(20px 20px 80px rgba(16, 5, 48, 0.08))",
          }}
          {...image}
        />
      </div>
    </Grid>
  );
};
